import React, { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIntl } from 'react-intl';

import Dialog from 'carbon-react/lib/components/dialog';
import Typography from 'carbon-react/lib/components/typography';

import { Sidebar } from '@futrli/ui-components';

import { AIPredictionChat } from './ai-prediction-chat/ai-prediction-chat';
import { FormulaPrediction } from './formula-prediction/formula-prediction';
import { FreestylePrediction } from './freestyle-prediction/freestyle-prediction';
import { PredictionType } from '../../types';
import { RepeatingPrediction } from './repeating-prediction/repeating-prediction';
import { trackEvent } from '../../../../core/tracker';
import { TRACKING_EVENTS } from '../../../../core/constants';
import { UnitPrediction } from './unit-prediction/unit-prediction';
import { useStandardOrganisationPredictionBuilderContext } from '../../context/standard-organisation-prediction-builder-context';

import styles from '../prediction-drawer.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const StandardOrganisationPredictionDrawer = ({ isOpen, onClose }: Props) => {
  const { isEditing, predictionType } = useStandardOrganisationPredictionBuilderContext();
  const { aiFormulaBuilder } = useFlags();
  const intl = useIntl();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    trackEvent(TRACKING_EVENTS.OPEN_STANDARD_ORG_MANUAL_PREDICTION_SIDEBAR);
  }, [isOpen]);

  const title = intl.formatMessage({ id: 'predict.prediction_builder', defaultMessage: 'Prediction builder' });

  const content = isEditing ? (
    <>
      {predictionType === PredictionType.FREESTYLE && <FreestylePrediction onClose={onClose} />}
      {predictionType === PredictionType.FORMULA && <FormulaPrediction onClose={onClose} />}
      {predictionType === PredictionType.REPEATING && <RepeatingPrediction onClose={onClose} />}
      {predictionType === PredictionType.UNIT && <UnitPrediction onClose={onClose} />}
    </>
  ) : (
    <>
      <FreestylePrediction onClose={onClose} isPredictionTypeActive={predictionType === PredictionType.FREESTYLE} />
      <FormulaPrediction onClose={onClose} isPredictionTypeActive={predictionType === PredictionType.FORMULA} />
      <RepeatingPrediction onClose={onClose} isPredictionTypeActive={predictionType === PredictionType.REPEATING} />
      <UnitPrediction onClose={onClose} isPredictionTypeActive={predictionType === PredictionType.UNIT} />
    </>
  );

  const dialogContent = <div className={styles.predictionBuilderDialogContent}>{content}</div>;

  if (aiFormulaBuilder) {
    return (
      <Dialog
        open={isOpen}
        className={styles.predictionBuilderDrawer}
        data-testid="snapshot-drawer"
        aria-label={title}
        size="fullscreen"
        onCancel={onClose}
        title={title}
      >
        {predictionType === PredictionType.FORMULA ? (
          <div className={styles.predictionBuilderDialogColumns}>
            <div className={styles.predictionBuilderDialogLeftColumn}>{dialogContent}</div>
            <div className={styles.predictionBuilderDialogRightColumn}>
              <AIPredictionChat />
            </div>
          </div>
        ) : (
          <div className={styles.predictionBuilderDialogSingleColumn}>{dialogContent}</div>
        )}
      </Dialog>
    );
  }

  return (
    <Sidebar
      open={isOpen}
      className={styles.predictionBuilderDrawer}
      data-testid="snapshot-drawer"
      aria-label={title}
      width="85%"
      onCancel={onClose}
      header={
        <Typography variant="h2" fontWeight="700">
          {title}
        </Typography>
      }
    >
      {content}
    </Sidebar>
  );
};
